import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Button from '../ui/Button';
import purchaseOrderService, { type PurchaseOrderXeroSettings as Settings, type XeroBankAccount } from '../../services/purchaseOrders';
import xeroConnectionService from '../../services/xeroConnection';
import { XeroContactPicker } from '../nursery/accounts/XeroContactCard';
import { errorMessage } from '../../utils/apiResponse';
import type { XeroAccount, XeroTaxRate } from '../../types/nurseryAccounts';

/**
 * The Purchase orders section of Accounts → Xero.
 *
 * Switched on, an order goes to the organisation's Xero once it is sent to
 * the supplier or marked as ordered: approved, under our number, to the
 * supplier's contact, with its lines on the account and tax rate chosen here.
 * The supplier's bill is then made from it in Xero, and Xero marks it billed.
 *
 * Any accounts seat reads this; an Accounts Admin changes it. Same connection
 * and permissions as invoices, so an organisation connected for payroll only
 * reconnects first.
 *
 * Two more settings keep Xero's supplier list in hand (2026-10-09):
 *  - the sundry contact: an order to a supplier that is only typed on it, not
 *    on Accounts → Suppliers, goes to this one contact, never a new one;
 *  - the card account: an order marked "Paid on the company card" goes as
 *    Spend Money from it, with its receipts attached. That needs two more Xero
 *    permissions, so choosing it asks to reconnect.
 */

interface Props {
  /** The Xero sign-in exists. */
  connected: boolean;
  /** The connection holds the accounting permissions and has a Xero organisation. */
  ready: boolean;
  isAdmin: boolean;
  /** Start a fresh Xero sign-in (the page's Reconnect). */
  onReconnect: () => void;
  reconnecting: boolean;
}

const INPUT = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';
const LABEL = 'block text-sm font-medium text-neutral-700 mb-1';

export default function PurchaseOrderXeroSettings({ connected, ready, isAdmin, onReconnect, reconnecting }: Props) {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [form, setForm] = useState({ enabled: false, account_code: '', tax_type: 'INPUT', card_account_id: '' });
  const [accounts, setAccounts] = useState<XeroAccount[]>([]);
  const [bankAccounts, setBankAccounts] = useState<XeroBankAccount[]>([]);
  const [pickingSundry, setPickingSundry] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [taxRates, setTaxRates] = useState<XeroTaxRate[]>([]);
  const [listsError, setListsError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const show = useCallback((s: Settings) => {
    setSettings(s);
    setForm({ enabled: s.enabled, account_code: s.account_code ?? '', tax_type: s.tax_type ?? 'INPUT', card_account_id: s.card_account_id ?? '' });
  }, []);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await purchaseOrderService.getXeroSettings();
        if (live && res.success && res.data) show(res.data);
      } catch (e) {
        if (live) setError(errorMessage(e, 'Could not load the purchase order settings'));
      }
      if (!isAdmin || !ready) return;
      try {
        const lists = await purchaseOrderService.getXeroLists();
        if (!live) return;
        if (lists.success && lists.data) {
          setAccounts(lists.data.accounts || []);
          setTaxRates(lists.data.tax_rates || []);
          setBankAccounts(lists.data.bank_accounts || []);
        } else setListsError(errorMessage(lists, 'Could not read the accounts from Xero'));
      } catch (e) {
        if (live) setListsError(errorMessage(e, 'Could not read the accounts from Xero'));
      }
    })();
    return () => { live = false; };
  }, [isAdmin, ready, show]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const res = await purchaseOrderService.saveXeroSettings({
        enabled: form.enabled,
        account_code: form.account_code || null,
        tax_type: form.tax_type || null,
        card_account_id: form.card_account_id || null,
      });
      if (res.success && res.data) {
        show(res.data);
        const n = res.data.queued ?? 0;
        setNotice(!res.data.enabled
          ? 'Saved. Purchase orders are not sent to Xero.'
          : n > 0
            ? `Saved. ${n} order${n === 1 ? ' already with its supplier is' : 's already with their suppliers are'} on the way to Xero, and new orders follow as they are sent.`
            : 'Saved. Orders go to Xero as they are sent to the supplier or marked as ordered.');
      } else setError(errorMessage(res, 'Could not save'));
    } catch (e) {
      setError(errorMessage(e, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  // Card purchases need Spend Money and attachments, asked for on a fresh sign-in.
  const reconnectForCards = async () => {
    setConnecting(true);
    setError(null);
    try {
      const res = await xeroConnectionService.getAuthorizeUrl(false, true);
      if (res.success && res.data?.authorization_url) window.location.href = res.data.authorization_url;
      else setError(errorMessage(res, 'Could not open Xero'));
    } catch (e) {
      setError(errorMessage(e, 'Could not open Xero'));
    } finally {
      setConnecting(false);
    }
  };

  const saveSundry = async (contactId: string | null): Promise<string | null> => {
    const res = await purchaseOrderService.saveXeroSettings({ sundry_contact_id: contactId });
    if (!res.success || !res.data) return errorMessage(res, 'Could not save the sundry contact');
    show(res.data);
    setPickingSundry(false);
    setNotice(contactId
      ? `Saved. Orders to a supplier not on your list go to ${res.data.sundry_contact_name ?? 'that contact'} in Xero.`
      : 'Saved. Orders to a supplier not on your list wait until it is added there.');
    return null;
  };

  const nameOf = (code: string | null) => {
    if (!code) return null;
    const a = accounts.find((x) => x.code === code);
    return a ? `${a.code} — ${a.name}` : code;
  };
  const knownAccount = accounts.some((a) => a.code === form.account_code);
  const knownTax = taxRates.some((t) => t.tax_type === form.tax_type);

  return (
    <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
      <h2 className="text-base font-semibold text-neutral-900 break-words">Send purchase orders</h2>
      <p className="text-sm text-neutral-700 break-words">
        Each purchase order goes to Xero once it is sent to the supplier or marked as ordered: approved, under its own
        number (such as PO-HOME-3-004), to the supplier&rsquo;s contact. When the supplier&rsquo;s bill arrives, make it
        from the order in Xero (New bill, then Copy from purchase order), and Xero marks the order billed. That shows
        on the order here too.
      </p>

      {!connected ? (
        <p className="text-sm text-neutral-500 break-words">Connect Xero above to send purchase orders to it.</p>
      ) : !ready ? (
        <div className="space-y-2">
          <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 break-words">
            This connection cannot reach the books, so purchase orders cannot go to it yet. Reconnect Xero: your
            organisation shows already selected there, so press Continue. Nothing already set up is lost.
          </p>
          {isAdmin && (
            <Button size="sm" onClick={onReconnect} disabled={reconnecting}>
              {reconnecting ? 'Opening Xero…' : 'Reconnect Xero'}
            </Button>
          )}
        </div>
      ) : settings && (
        <>
          {notice && <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2 break-words" role="status">{notice}</p>}
          {error && <p role="alert" className="text-sm text-red-700 break-words">{error}</p>}

          {isAdmin ? (
            <>
              <label className="flex items-start gap-2 text-sm text-neutral-800">
                <input type="checkbox" className="mt-0.5 h-4 w-4" checked={form.enabled}
                  onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} />
                <span className="min-w-0 break-words">
                  Send purchase orders to Xero.{' '}
                  {!settings.enabled && 'Orders already with their suppliers go too, when you save.'}
                </span>
              </label>
              {listsError && <p className="text-sm text-red-700 break-words">{listsError}</p>}
              <div className="min-w-0">
                <label htmlFor="po-xero-account" className={LABEL}>Account (optional)</label>
                <select id="po-xero-account" value={form.account_code} className={INPUT}
                  onChange={(e) => setForm((f) => ({ ...f, account_code: e.target.value }))}>
                  <option value="">Chosen on each bill in Xero</option>
                  {form.account_code && !knownAccount && <option value={form.account_code}>{form.account_code}</option>}
                  {accounts.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
                </select>
                <p className="mt-1 text-xs text-neutral-500 break-words">
                  Every line of every order goes to this account, such as chemicals and farm supplies. Change a line on
                  the bill in Xero where it belongs elsewhere.
                </p>
              </div>
              <div className="min-w-0">
                <label htmlFor="po-xero-tax" className={LABEL}>Tax rate</label>
                <select id="po-xero-tax" value={form.tax_type} className={INPUT}
                  onChange={(e) => setForm((f) => ({ ...f, tax_type: e.target.value }))}>
                  {form.tax_type && !knownTax && <option value={form.tax_type}>{form.tax_type === 'INPUT' ? 'GST on Expenses (INPUT)' : form.tax_type}</option>}
                  {taxRates.map((t) => (
                    <option key={t.tax_type} value={t.tax_type}>{t.name} ({Number(t.rate)}%)</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-neutral-500 break-words">
                  Unit costs on purchase orders are before GST, and Xero adds this rate to them.
                </p>
              </div>
              <div className="min-w-0">
                <label htmlFor="po-xero-card" className={LABEL}>Card account, for card purchases</label>
                <select id="po-xero-card" value={form.card_account_id} className={INPUT}
                  onChange={(e) => setForm((f) => ({ ...f, card_account_id: e.target.value }))}>
                  <option value="">Not used</option>
                  {form.card_account_id && !bankAccounts.some((b) => b.account_id === form.card_account_id) && (
                    <option value={form.card_account_id}>{settings.card_account_name ?? 'The account chosen before'}</option>
                  )}
                  {bankAccounts.map((b) => (
                    <option key={b.account_id} value={b.account_id}>
                      {b.name}{b.is_credit_card ? ' (credit card)' : ''}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-neutral-500 break-words">
                  An order marked &ldquo;Paid on the company card&rdquo; goes to Xero as Spend Money from this account, with
                  its receipt attached, ready to reconcile against the card statement. It needs the account above, and a
                  cost on every line.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
              </div>
            </>
          ) : (
            <>
              <dl className="space-y-2">
                <div className="min-w-0">
                  <dt className="text-xs text-neutral-500">Sent to Xero</dt>
                  <dd className="text-sm text-neutral-800">{settings.enabled ? 'Yes' : 'No'}</dd>
                </div>
                <div className="min-w-0">
                  <dt className="text-xs text-neutral-500">Account</dt>
                  <dd className="text-sm text-neutral-800 break-words">{nameOf(settings.account_code) ?? 'Chosen on each bill in Xero'}</dd>
                </div>
                <div className="min-w-0">
                  <dt className="text-xs text-neutral-500">Tax rate</dt>
                  <dd className="text-sm text-neutral-800 break-words">{settings.tax_type ?? 'GST on Expenses'}</dd>
                </div>
                <div className="min-w-0">
                  <dt className="text-xs text-neutral-500">Card account</dt>
                  <dd className="text-sm text-neutral-800 break-words">{settings.card_account_name ?? 'Not used'}</dd>
                </div>
              </dl>
              <p className="text-sm text-neutral-500 break-words">An Accounts Admin can change this.</p>
            </>
          )}

          {settings.card_account_id && settings.can_post_card === false && (
            <div className="space-y-2">
              <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 break-words">
                Card purchases cannot go to Xero yet: this connection was made without permission to record card
                payments and attach receipts. Reconnect Xero and approve them. Your organisation shows already selected
                there, so press Continue.
              </p>
              {isAdmin && (
                <Button size="sm" onClick={reconnectForCards} disabled={connecting}>
                  {connecting ? 'Opening Xero…' : 'Reconnect Xero for card purchases'}
                </Button>
              )}
            </div>
          )}

          <div className="min-w-0 border-t border-neutral-100 pt-3 space-y-2">
            <h3 className="text-sm font-semibold text-neutral-900">Suppliers not on your list</h3>
            <p className="text-sm text-neutral-700 break-words">
              A supplier on Accounts → Suppliers has its own contact in Xero. One only typed on an order goes to a single
              sundry contact instead, its name in the reference, so one-off purchases do not fill your Xero supplier list.
            </p>
            <p className="text-sm text-neutral-800 break-words">
              Sundry contact: <span className="font-medium">{settings.sundry_contact_name ?? 'none chosen'}</span>
              {!settings.sundry_contact_name && ' — orders to a supplier not on your list wait until you choose one or add the supplier.'}
            </p>
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={() => setPickingSundry(true)}>
                {settings.sundry_contact_id ? 'Change the sundry contact' : 'Choose a sundry contact'}
              </Button>
            )}
          </div>

          {settings.enabled && (
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {([
                ['Waiting to go', settings.queue.pending, false],
                ['Not sent', settings.queue.failed, true],
                ['Billed in Xero', settings.queue.billed, false],
              ] as [string, number, boolean][]).map(([label, count, bad]) => (
                <div key={label} className="min-w-0 border border-neutral-200 rounded-lg p-3">
                  <dt className="text-xs text-neutral-500 break-words">{label}</dt>
                  <dd className={`text-xl font-semibold ${bad && Number(count) > 0 ? 'text-red-700' : 'text-neutral-900'}`}>
                    {Number(count) || 0}
                  </dd>
                </div>
              ))}
            </dl>
          )}

          {settings.failures.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-neutral-900">Not sent to Xero</h3>
              <ul className="space-y-2">
                {settings.failures.map((f) => (
                  <li key={f.id} className="border border-red-200 bg-red-50 rounded-lg p-3 space-y-1">
                    <p className="text-sm text-neutral-900 break-words">
                      <Link to={`/purchase-orders/${f.id}`} className="font-semibold text-primary-700 hover:underline">
                        {f.reference_number ?? `PO #${f.id}`}
                      </Link>
                      {f.supplier_name ? ` · ${f.supplier_name}` : ''}
                    </p>
                    <p className="text-sm text-red-700 break-words">{f.xero_sync_error || 'Xero gave no reason.'}</p>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-neutral-500 break-words">Open an order to send it again.</p>
            </div>
          )}
        </>
      )}

      {pickingSundry && settings && (
        <XeroContactPicker
          name={settings.sundry_contact_name ?? 'Sundry'}
          intro="Orders to a supplier that is not on your list go to the contact you choose, with the supplier's own name in the reference. Many organisations keep one called Sundry suppliers or Cash purchases."
          unlinked="Without one, an order to a supplier not on your list waits until it is added there."
          currentContactId={settings.sundry_contact_id ?? null}
          onClose={() => setPickingSundry(false)}
          onSave={saveSundry}
        />
      )}
    </section>
  );
}
